import { Area, AreaChart, Tooltip, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardHeader, Typography, useRouter } from '@forgeglobal/design-framework'
import { StyleDefaults } from 'isolated-design-library'
import { formatAbbreviatedMoney, formatAbbreviatedNumber } from 'util/NumberUtils'
import { useIssuerGraphQuery } from 'graphql/bindings/AdminBindings'
import React, { useRef, useState } from 'react'
import ResizeSensor from 'resize-sensor'
import flatMap from 'lodash/flatMap'

const toFixedFloat = (value: number) => {
  return parseFloat(value.toFixed(2))
}

export interface Block {
  price_bucket_high: number
  price_bucket_low: number
  volume: number
}
export interface SupplyDemandGraphBaseProps {
  orderBookBidBlocks: Block[]
  orderBookAskBlocks: Block[]
}

export const SupplyDemandGraphBase: React.FC<SupplyDemandGraphBaseProps> = ({
  orderBookAskBlocks,
  orderBookBidBlocks,
}) => {
  const [chartWidth, setChartWidth] = useState(400)
  const ref = useRef<HTMLDivElement | null>(null)
  const bidBlocks = orderBookBidBlocks.sort((a, b) => {
    return a.price_bucket_low > b.price_bucket_low ? -1 : 1
  })
  const askBlocks = orderBookAskBlocks.sort((a, b) => {
    return a.price_bucket_low > b.price_bucket_low ? 1 : -1
  })
  let bidTotalVolume = 0
  const bidCurve = flatMap(bidBlocks, (block, index) => {
    bidTotalVolume += block.volume
    if (index === 0) {
      return [
        { price: toFixedFloat(block.price_bucket_high), bidVolume: 0 },
        { price: toFixedFloat(block.price_bucket_high), bidVolume: bidTotalVolume },
        { price: toFixedFloat(block.price_bucket_low), bidVolume: bidTotalVolume },
      ]
    }
    return [{ price: toFixedFloat(block.price_bucket_low), bidVolume: bidTotalVolume }]
  }).reverse()
  let askTotalVolume = 0
  const askCurve = flatMap(askBlocks, (block, index) => {
    askTotalVolume += block.volume
    if (index === askBlocks.length - 1) {
      return [
        { price: toFixedFloat(block.price_bucket_low), askVolume: askTotalVolume },
        { price: toFixedFloat(block.price_bucket_high), askVolume: askTotalVolume },
      ]
    }
    if (index === 0) {
      return [
        { price: toFixedFloat(block.price_bucket_low), askVolume: 0 },
        { price: toFixedFloat(block.price_bucket_low), askVolume: askTotalVolume },
      ]
    }
    return [{ price: toFixedFloat(block.price_bucket_low), askVolume: askTotalVolume }]
  })
  const updateWidth = () => {
    setChartWidth(ref?.current?.clientWidth ?? 400)
  }
  return (
    <Card>
      <CardHeader title="Supply & Demand Graph" />
      <CardContent>
        <Typography variant="caption" style={{ fontWeight: 300 }}>
          <div
            ref={rf => {
              ref.current = rf
              rf && new ResizeSensor(rf, updateWidth)
            }}
          />
          <AreaChart
            data={[...bidCurve, ...askCurve]}
            width={chartWidth}
            height={280}
            margin={{ left: 8, right: 32, bottom: 8 }}>
            <defs>
              <linearGradient id="colorBid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10de1d" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#10de1d" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorAsk" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1f62ff" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#1f62ff" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="price"
              type="number"
              label={{
                value: 'Price',
                position: 'insideBottom',
                offset: -5,
                fill: StyleDefaults.palette.text.secondary,
              }}
              domain={['dataMin', 'dataMax']}
              tickFormatter={value => '$' + toFixedFloat(parseFloat(`${value}`))}
              tickCount={8}
              allowDecimals={true}
              stroke={StyleDefaults.palette.text.secondary}
            />
            {/* <YAxis
            tickFormatter={value => formatAbbreviatedNumber(parseInt(`${value}`))}
            dataKey="askVolume"
            yAxisId="0"
            label={{ value: 'Ask Volume', angle: 90, position: 'insideLeft', offset: 55 }}
            orientation="right"
          />
          <YAxis
            tickFormatter={value => formatAbbreviatedNumber(parseInt(`${value}`))}
            label={{ value: 'Bid Volume', angle: -90, position: 'insideRight', offset: 50 }}
            yAxisId="1"
            dataKey="bidVolume"
            orientation="left"
          /> */}
            <YAxis
              tickFormatter={value => formatAbbreviatedMoney(parseInt(`${value}`))}
              label={{
                value: 'Volume',
                angle: -90,
                position: 'insideRight',
                offset: 60,
                fill: StyleDefaults.palette.text.secondary,
              }}
              orientation="left"
              stroke={StyleDefaults.palette.text.secondary}
            />
            <Area
              type="stepAfter"
              dataKey="bidVolume"
              name="Bid Volume"
              stroke="#10de1d"
              strokeWidth="2"
              fill="url(#colorBid)"
              // yAxisId="1"
              isAnimationActive={false}
            />
            <Area
              type="stepAfter"
              dataKey="askVolume"
              name="Ask Volume"
              stroke="#1f62ff"
              strokeWidth="2"
              fill="url(#colorAsk)"
              isAnimationActive={false}
            />
            <Tooltip formatter={value => formatAbbreviatedNumber(parseInt(`${value}`))} isAnimationActive={false} />
          </AreaChart>
        </Typography>
      </CardContent>
    </Card>
  )
}

export const IssuerSupplyDemandGraph: React.FC = () => {
  const { match } = useRouter<{ id: string }>()
  const { data } = useIssuerGraphQuery({ variables: { issuerId: match.params.id } })
  const company = data?.account?.issuer_account?.company
  return (
    <SupplyDemandGraphBase
      orderBookAskBlocks={company?.order_book_ask_blocks ?? []}
      orderBookBidBlocks={company?.order_book_bid_blocks ?? []}
    />
  )
}
